import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useGetAccountFioVersionMigrationStatusQuery } from '@frameio/graphql-client/generated';
import { migrationStatusWithActiveMigrationGate } from '@frameio/shared/account-migration/helpers';
import { useMigrationStartedAt } from '@frameio/shared/account-migration/hooks/useMigrationStartedAt';
import { useForceAccountId } from '@frameio/shared/contexts/ActiveAccountIdContext/ForcedAccountIdFromHistoryState';
import { getLink } from '@frameio/shared/routing/getLink';
import { getV3Url } from '@frameio/shared/routing/redirectToV3';
import {
  Avatar,
  CheckedSVG,
  MenuButton,
  Text,
  theme,
  useHasMounted,
  useHoverIntent,
} from '@frameio/vapor';
import styles from './AccountOption.module.css';
import { V4_ACCOUNT_VERSION } from './constants';
import TEXT from './generated/TEXT/AccountOption';
import type { onAccountChangedFn } from './types';
import { usePreloadAccount } from './usePreloadAccount';

TEXT.DEFINE({
  /** Status chip label shown next to an account when it is on the legacy (v3) experience */
  labelForLegacyChip: 'LEGACY',
});

export const AccountOption = <
  AccountForAccountOption extends {
    id: string;
    displayName?: string | null;
    imageUrl?: string | null;
    version?: number | null;
    __typename?: 'InboxAccount' | 'Account';
  },
>(
  props: {
    testId: string;
    account: AccountForAccountOption;
    isActive?: boolean;
  } & (
    | {
        onAccountSelected: onAccountChangedFn;
        disabled?: false;
      }
    | {
        onAccountSelected?: never;
        disabled: true;
      }
  )
) => {
  const { onAccountSelected } = props;
  const accountId = props.account.id;
  const router = useRouter();
  const hasMounted = useHasMounted();

  const preloadAccount = usePreloadAccount();
  const forceAccountId = useForceAccountId();

  // Which version is this account
  const isAccountV4 = props.account.version === V4_ACCOUNT_VERSION;
  const isInboxAccount = props.account.__typename === 'InboxAccount';

  const { data: migrationStatusData } =
    useGetAccountFioVersionMigrationStatusQuery({
      variables: { accountId },
      skip: isAccountV4 || !hasMounted,
      fetchPolicy: 'cache-first',
    });

  const migrationStartedAt = useMigrationStartedAt({
    accountId,
    skip: isAccountV4 || !hasMounted,
  });

  const migrationStatusTypename =
    migrationStatusData?.fioVersionMigration?.status?.__typename;
  const isV3AndNotMigrating =
    !isAccountV4 &&
    migrationStartedAt === null &&
    !migrationStatusWithActiveMigrationGate(migrationStatusTypename);

  const showLegacyLozenge =
    !isAccountV4 &&
    !migrationStartedAt &&
    migrationStatusTypename !== 'FioVersionMigrationMustMigrate';

  // Preload the account, if V4
  const { bind: bindHoverIntentHandlers } = useHoverIntent(() => {
    // Skip preloading for Inbox Accounts
    if (!isAccountV4 || isInboxAccount) return;

    preloadAccount(accountId);
    router.prefetch(getLink({ page: 'workspaces', accountId }));
  });

  // Get the URL for this account, based on version and type of account
  const href = useMemo(() => {
    if (isV3AndNotMigrating) {
      return getV3Url(props.account.id);
    }

    if (isInboxAccount) {
      return getLink({ page: 'inbox', accountId: props.account.id });
    }

    return getLink({ page: 'workspaces', accountId: props.account.id });
  }, [isV3AndNotMigrating, isInboxAccount, props.account.id]);

  const { labelForLegacyChip } = TEXT;
  const chip = useMemo(() => {
    if (showLegacyLozenge) {
      return (
        <Text
          className={styles.chip}
          color="inherit"
          enableTooltip={true}
          fontWeight="medium"
          truncate={true}
          variant="caption"
        >
          {labelForLegacyChip}
        </Text>
      );
    }
    return null;
  }, [showLegacyLozenge, labelForLegacyChip]);

  const avatar = (
    <Avatar
      alt={props.account.displayName ?? ''}
      shape="square"
      src={props.account.imageUrl ?? undefined}
    />
  );

  const renderIconAfter = useCallback(() => {
    return (
      <CheckedSVG
        $isChecked={props.isActive}
        color={theme.colors.brand.primary}
      />
    );
  }, [props.isActive]);

  const onPress = useCallback(() => {
    if (isV3AndNotMigrating) {
      // Redirect immediately
      globalThis.location.assign(href);
      return;
    }

    if (href.includes('inbox')) {
      router.push(href);
    } else {
      forceAccountId(accountId);
    }

    // Optimistically show the new account immediately
    onAccountSelected?.(accountId);
  }, [
    accountId,
    forceAccountId,
    href,
    isV3AndNotMigrating,
    onAccountSelected,
    router,
  ]);

  const onClick = useCallback((e: MouseEvent) => {
    // User native browser behavior for the link if modifier keys are pressed
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) {
      return;
    }
    // Prevent hard navigation, use onSelect instead
    e.preventDefault();
  }, []);

  return (
    <MenuButton
      aria-checked={props.isActive}
      // Use anchor tag so the URL is seen in the browser status bar on hover
      as={props.disabled ? undefined : 'a'}
      data-testid={props.testId}
      disabled={props.disabled}
      href={href}
      iconAfter={renderIconAfter}
      iconBefore={avatar}
      name={accountId}
      onClick={onClick}
      onPress={onPress}
      {...bindHoverIntentHandlers}
    >
      <div className={styles.container}>
        <Text enableTooltip={true} truncate={true} variant="body">
          {props.account.displayName}
        </Text>
        {chip}
      </div>
    </MenuButton>
  );
};
